// src/components/quotes/QuoteAcceptedPanel.jsx
//
// WHAT THE CLIENT ACCEPTED — shown to Craig, in the builder, as an order list.
//
// Craig: "if a client includes an optional item, or changes the selected
// option, and they then accept, all the changes are saved and shown to me in
// the accepted quote somehow, so I know what to order and what the customer
// has accepted."
//
// Saved: always was (the acceptance snapshot). Shown: this. It reads the
// staff-side GET /api/quotes/:id/view-activity and renders the snapshot's
// frozen summary — the chosen option per section, dropped optional lines
// STRUCK rather than omitted, changed quantities beside their originals, the
// accepted total, who, when (Perth), their message, and the terms as accepted.
//
// ⚠ READ-ONLY BY DESIGN, and everything here is written by public routes that
// cannot reach the state blob. The ONE thing this component writes back is
// quote.status — "won", once, stamped — through the reconciliation effect,
// exactly as ProposalsTab does. Acceptance still mints nothing; raising the
// invoice stays Craig's explicit action, and the panel says so.
//
// ⚠ THE PANEL MUST NOT MOVE when the quote is edited afterwards. It renders the
// snapshot, never the builder's quote, so it cannot: the only prop read from
// the live quote is its id (to fetch) and its status (to reconcile).
import React from "react";
import { fetchQuoteActivity } from "../../api/quotesApi";
import {
  needsWonReconciliation, wonReconciliationPatch, whenPerth, money,
} from "./quoteAcceptance";

const LABEL = { fontSize: 10, color: "var(--text3)", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" };
const MUTED = { fontSize: 11, color: "var(--text3)", lineHeight: 1.6 };

/* One accepted line. Struck when the customer dropped it; the quantity shows
   "was N" when they changed it, because a silently-different number is the
   one that gets mis-ordered. */
function Line({ l }) {
  const off = !!l.excluded;
  const cell = { padding: "4px 6px", fontSize: 11, verticalAlign: "top", borderTop: "1px solid var(--border)",
                 color: off ? "var(--text3)" : "var(--text)", textDecoration: off ? "line-through" : "none" };
  const num = { ...cell, textAlign: "right", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" };
  return (
    <tr>
      <td style={cell}>
        {l.description || "(no description)"}
        {(l.sku || l.manufacturer) && (
          <div style={{ fontSize: 10, color: "var(--text3)", textDecoration: "none" }}>
            {[l.manufacturer, l.sku].filter(Boolean).join(" · ")}
          </div>
        )}
      </td>
      <td style={num}>
        {l.quantityChanged ? (
          <span title={`Quoted as ${l.originalQuantity}; the client changed it to ${l.quantity}`}
                style={{ background: "#fef3c7", color: "#92400e", border: "1px solid #fcd34d",
                         borderRadius: 4, padding: "0 5px", fontWeight: 700, textDecoration: "none" }}>
            {l.quantity} <span style={{ fontWeight: 400 }}>(was {l.originalQuantity})</span>
          </span>
        ) : l.quantity}
      </td>
      <td style={num}>{money(l.unitPrice)}</td>
      <td style={{ ...num, textDecoration: "none", fontStyle: off ? "italic" : "normal" }}>
        {off ? "Removed" : money(l.lineTotal)}
      </td>
    </tr>
  );
}

function Lines({ lines }) {
  if (!lines || !lines.length) return null;
  const th = { ...LABEL, padding: "2px 6px", textAlign: "left" };
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 4 }}>
      <thead><tr>
        <th style={th}>Item</th>
        <th style={{ ...th, textAlign: "right" }}>Qty</th>
        <th style={{ ...th, textAlign: "right" }}>Unit</th>
        <th style={{ ...th, textAlign: "right" }}>Amount</th>
      </tr></thead>
      <tbody>{lines.map((l) => <Line key={l.id} l={l} />)}</tbody>
    </table>
  );
}

function Accepted({ acceptance, quote }) {
  const s = acceptance.summary;
  const t = acceptance.totals || (s && s.totals) || {};
  return (
    <div style={{ background: "#dcfce7", border: "1px solid #86efac", borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d" }}>
        Accepted {whenPerth(acceptance.acceptedAt)}
        {(acceptance.acceptedByName || acceptance.acceptedByEmail) &&
          ` by ${acceptance.acceptedByName || acceptance.acceptedByEmail}`}
        {acceptance.acceptedByName && acceptance.acceptedByEmail && (
          <span style={{ fontWeight: 400, color: "#166534" }}> · {acceptance.acceptedByEmail}</span>
        )}
      </div>

      {/* The delta, first. Craig's actual question is "what do I order", and
          the fastest honest answer is what changed — or that nothing did. */}
      {s && (
        <div style={{ fontSize: 12, fontWeight: 700, color: s.changed ? "#92400e" : "#166534",
                      background: s.changed ? "#fef3c7" : "transparent",
                      border: s.changed ? "1px solid #fcd34d" : "none",
                      borderRadius: 6, padding: s.changed ? "5px 8px" : "4px 0", marginTop: 6 }}>
          {s.headline}
        </div>
      )}

      {acceptance.text && (
        <div style={{ fontSize: 11, color: "#166534", marginTop: 6, lineHeight: 1.6 }}>“{acceptance.text}”</div>
      )}

      {/* Per section: WHICH OPTION, named — the thing Craig asked about first. */}
      {s && (s.sections || []).map((sec) => (
        <div key={sec.id} style={{ marginTop: 10, background: "rgba(255,255,255,0.7)", borderRadius: 6, padding: "6px 8px" }}>
          <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "var(--text)" }}>{sec.label || "Section"}</div>
            {sec.hidden && (
              <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 4, background: "#eef2f7", color: "#475569" }}>
                not itemised to the client
              </span>
            )}
            <div style={{ flex: 1 }} />
            <div style={{ fontSize: 11, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>{money(sec.subtotal)}</div>
          </div>
          {sec.isChoice && (
            <div style={{ fontSize: 11, marginTop: 2, color: sec.optionChanged ? "#92400e" : "var(--text2)" }}>
              {sec.optionChanged ? "Chose " : "Option: "}<strong>{sec.chosenLabel || "(unnamed option)"}</strong>
              {sec.optionChanged && sec.offeredLabel && (
                <span style={{ color: "var(--text3)" }}> — you had selected {sec.offeredLabel}</span>
              )}
            </div>
          )}
          <Lines lines={sec.lines} />
        </div>
      ))}
      {s && (s.commonLines || []).length > 0 && (
        <div style={{ marginTop: 10, background: "rgba(255,255,255,0.7)", borderRadius: 6, padding: "6px 8px" }}>
          <div style={{ fontSize: 11, fontWeight: 700 }}>Included in every option</div>
          <Lines lines={s.commonLines} />
        </div>
      )}

      {/* The accepted money. From the snapshot, so it cannot drift from what
          they agreed to even if the builder's totals now say otherwise.
          ⛔ A snapshot with a recurring stream shows TWO accepted figures —
          once-off and monthly — and never their sum: the sum is what would
          get invoiced as a lump. A snapshot from before the split carries no
          streams and reads exactly as it always did. */}
      <div style={{ marginTop: 10, display: "flex", justifyContent: "flex-end" }}>
        <table style={{ borderCollapse: "collapse", fontSize: 11 }}>
          <tbody>
            {(() => {
              const rec = t.recurring && t.recurring.lineCount > 0 ? t.recurring : null;
              const once = t.onceOff && t.onceOff.lineCount > 0 ? t.onceOff : null;
              const rows = (st, label, suffix, key) => (
                <React.Fragment key={key}>
                  <tr><td style={{ padding: "1px 10px 1px 0", color: "var(--text2)" }}>Subtotal (ex GST)</td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(st.subtotal)}{suffix}</td></tr>
                  <tr><td style={{ padding: "1px 10px 1px 0", color: "var(--text2)" }}>GST 10%</td>
                      <td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{money(st.gst)}{suffix}</td></tr>
                  <tr><td style={{ padding: "3px 10px 1px 0", fontWeight: 700, borderTop: "1px solid #86efac" }}>{label}</td>
                      <td style={{ textAlign: "right", fontWeight: 700, fontVariantNumeric: "tabular-nums", borderTop: "1px solid #86efac" }}>{money(st.totalIncGst)}{suffix}</td></tr>
                </React.Fragment>
              );
              const head = (text, key) => (
                <tr key={key}><td colSpan={2} style={{ padding: "8px 0 2px", fontSize: 9, fontWeight: 700, color: "#166534", textTransform: "uppercase", letterSpacing: "0.6px" }}>{text}</td></tr>
              );
              if (!rec) return rows(t, "Accepted total (inc GST)", "", "all");
              const per = ` per ${rec.period || "month"}`;
              return [
                ...(once ? [head("Once-off", "h1"), rows(once, "Accepted total (inc GST)", "", "once")] : []),
                head("Monthly", "h2"),
                rows(rec, "Accepted total (inc GST)", per, "rec"),
              ];
            })()}
          </tbody>
        </table>
      </div>

      {(acceptance.terms || []).length > 0 && (
        <details style={{ marginTop: 8 }}>
          <summary style={{ fontSize: 11, color: "#166534", cursor: "pointer" }}>
            Terms as accepted ({acceptance.terms.length})
          </summary>
          <ol style={{ margin: "6px 0 0 16px", padding: 0 }}>
            {acceptance.terms.map((c, i) => (
              <li key={i} style={{ fontSize: 10.5, color: "var(--text2)", lineHeight: 1.5, whiteSpace: "pre-wrap", marginBottom: 4 }}>{c}</li>
            ))}
          </ol>
        </details>
      )}

      {/* Stated outright, because it is the one rule a user would otherwise
          have to infer. */}
      <div style={{ fontSize: 10, color: "#166534", marginTop: 8, opacity: 0.85, lineHeight: 1.5 }}>
        {quote?.status === "won" ? "Marked Won automatically. " : ""}
        Nothing has been invoiced — raising the invoice is your action.
        {acceptance.summaryDerived && " This acceptance predates the change summary, so the list above was derived from the quote as it is now sent."}
      </div>
    </div>
  );
}

/**
 * Client activity for one quote: recipients and opens, questions, and — when
 * the client accepted — the accepted panel above. Fetches on the quote id
 * only; a different quote opening cancels the previous load.
 */
export default function QuoteAcceptedPanel({ quote, updateQuote }) {
  const id = quote?.id;
  const [activity, setActivity] = React.useState(null);
  const [loading, setLoading] = React.useState(false);

  const load = React.useCallback((quoteId) =>
    fetchQuoteActivity(quoteId).catch((err) => ({ published: false, error: err.message })), []);

  React.useEffect(() => {
    let cancelled = false;
    setActivity(null);
    if (!id) return undefined;
    setLoading(true);
    load(id).then((data) => {
      if (cancelled) return;
      setActivity(data);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [id, load]);

  /* Acceptance reconciliation — the ONE write. Fires once per quote, stamped;
     see quoteAcceptance.needsWonReconciliation for why the stamp matters. */
  React.useEffect(() => {
    if (!needsWonReconciliation(quote, activity)) return;
    updateQuote(quote.id, wonReconciliationPatch());
  }, [quote, activity, updateQuote]);

  const refresh = async () => {
    if (!id) return;
    setLoading(true);
    setActivity(await load(id));
    setLoading(false);
  };

  const published = !!activity?.published;
  const acceptance = published ? activity.acceptance : null;
  const qa = published ? (activity.qa || []) : [];
  const recipients = published ? (activity.recipients || []) : [];

  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <label style={LABEL}>Client activity</label>
        <div style={{ flex: 1 }} />
        {published && (
          <button className="btn" style={{ fontSize: 10, padding: "2px 8px" }} disabled={loading} onClick={refresh}>
            {loading ? "…" : "↻"}
          </button>
        )}
      </div>

      {!published ? (
        <div style={MUTED}>
          {loading ? "Loading…" : activity?.error
            ? `Couldn't load client activity: ${activity.error}`
            : "Not sent to a client link yet. Use “Send to client” above and every open, question and acceptance will show here."}
        </div>
      ) : (
        <>
          {acceptance?.accepted && <Accepted acceptance={acceptance} quote={quote} />}

          <div style={MUTED}>
            {recipients.length === 0 ? "No named recipients." : recipients.map((r) => (
              <div key={r.id}>
                <strong style={{ color: "var(--text2)" }}>{r.name || r.email}</strong>
                {r.name && r.email ? ` · ${r.email}` : ""}
                {" — "}
                {r.opens ? `opened ${r.opens}× · last ${whenPerth(r.lastOpenedAt)}` : "not opened yet"}
              </div>
            ))}
            {activity.unattributedOpens > 0 && (
              <div>Opened {activity.unattributedOpens}× through the shared link (unattributed).</div>
            )}
          </div>

          {qa.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <label style={LABEL}>Questions ({qa.length})</label>
              {qa.map((e) => (
                <div key={e.id} style={{ borderTop: "1px solid var(--border)", padding: "5px 0", fontSize: 11 }}>
                  <div style={{ fontWeight: 600 }}>
                    {e.question}
                    <span style={{ fontWeight: 400, color: "var(--text3)" }}>
                      {" — "}{e.askerName || e.askerEmail || "unnamed"} · {whenPerth(e.askedAt)}
                    </span>
                  </div>
                  {e.answer
                    ? <div style={{ color: "#0b5f7a", marginTop: 2 }}>{e.answer}</div>
                    : <div style={{ color: "#b45309", marginTop: 2 }}>Awaiting your reply — use the link in the notification email.</div>}
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
